import styled from '@emotion/styled';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';

import { EliceDottedPagination } from '../dotted-pagination';
import {
  gray50Color,
  gray200Color,
  main300Color,
  main400Color,
  textTeriaryColor,
} from '../tokenColorTemporary';

import type { DottedPaginationProps } from '../dotted-pagination/DottedPagination';

export type EiceTableProps = {
  type?: 'default' | 'single';
  rows: object[];
  columnNames: string[];
  tableTitleProps?: any;
  tableValueProps?: any;
  pagination?: DottedPaginationProps;
};

const StyledScrollBarStack = styled(Stack)<{
  scrollbarTrackColor?: string;
  scrollbarThumbColor: string;
  scrollbarHoverColor: string;
}>`
  width: fit-content;
  max-width: 100%;
  position: relative;
  overflow-x: auto;
  box-sizing: border-box;
  border-radius: 1.375rem;
  gap: 0;

  ::-webkit-scrollbar {
    height: 0.25rem;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: ${props => props.scrollbarTrackColor ?? gray200Color};
    border-radius: 0.5rem;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: ${props => props.scrollbarThumbColor ?? main300Color};
    border-radius: 0.5rem;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.scrollbarHoverColor ?? main400Color};
  }
`;

const EliceTable = ({
  type = 'default',
  rows,
  columnNames,
  tableTitleProps,
  tableValueProps,
  pagination,
}: EiceTableProps) => {
  const theme = useTheme();
  if (type === 'default') {
    return (
      <Stack gap="2.5rem" alignItems="center">
        <StyledScrollBarStack
          scrollbarThumbColor={theme.palette.primary.main}
          scrollbarHoverColor={theme.palette.secondary.main}
          border={`1px solid ${gray50Color}`}
        >
          <Table>
            <TableHead
              style={{
                backgroundColor: theme.palette.primary.light,
              }}
            >
              <TableRow>
                {columnNames.map((name: string, index: number) => {
                  return (
                    <TableCell
                      align="center"
                      style={{
                        height: '3.625rem',
                        minWidth: '13.5rem',
                        padding: '1rem',
                        boxSizing: 'border-box',
                        borderBottom: `1px solid ${theme.palette.secondary.main}`,
                        borderRight:
                          index < columnNames.length - 1
                            ? `1px solid ${theme.palette.secondary.main}`
                            : 'none',
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: theme.palette.primary.dark,
                      }}
                      {...tableTitleProps}
                    >
                      {name}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row: object, index: number) => (
                <TableRow key={`row${index}`}>
                  {Object.values(row).map((value: any, index: number) => {
                    return (
                      <TableCell
                        component="th"
                        scope="row"
                        align="center"
                        style={{
                          height: '3.625rem',
                          minWidth: '13.5rem',
                          padding: '1rem',
                          boxSizing: 'border-box',
                          borderBottom: `1px solid ${theme.palette.secondary.main}`,
                          borderRight:
                            index < Object.values(row).length - 1
                              ? `1px solid ${theme.palette.secondary.main}`
                              : 'none',
                          fontSize: '1rem',
                          fontWeight: 500,
                          color: textTeriaryColor,
                        }}
                        {...tableValueProps}
                      >
                        {value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledScrollBarStack>
        {pagination && <EliceDottedPagination {...pagination} />}
      </Stack>
    );
  } else {
    return (
      <Stack
        direction="row"
        boxSizing="border-box"
        borderRadius="1.375rem"
        border={`1px solid ${theme.palette.secondary.main}`}
        overflow="hidden"
        width="fit-content"
      >
        <Stack
          gap={0}
          style={{
            backgroundColor: theme.palette.primary.light,
          }}
        >
          {columnNames.map((name: string, index: number) => {
            return (
              <TableCell
                align="center"
                style={{
                  height: '3.625rem',
                  minWidth: '13.5rem',
                  padding: '1rem',
                  boxSizing: 'border-box',
                  borderRight: `1px solid ${theme.palette.secondary.main}`,
                  borderBottom:
                    index < columnNames.length - 1
                      ? `1px solid ${theme.palette.secondary.main}`
                      : 'none',
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: theme.palette.primary.dark,
                }}
                {...tableTitleProps}
              >
                {name}
              </TableCell>
            );
          })}
        </Stack>
        <Stack gap={0}>
          {Object.values(rows[0]).map((value: any, index: number) => {
            return (
              <TableCell
                component="th"
                scope="row"
                align="center"
                style={{
                  height: '3.625rem',
                  minWidth: '13.5rem',
                  padding: '1rem',
                  boxSizing: 'border-box',
                  borderBottom:
                    index < Object.values(rows[0]).length - 1
                      ? `1px solid ${theme.palette.secondary.main}`
                      : 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: textTeriaryColor,
                }}
                {...tableValueProps}
              >
                {value}
              </TableCell>
            );
          })}
        </Stack>
      </Stack>
    );
  }
};

export default EliceTable;
